"use client";

import type { MedplumClient } from "@medplum/core";
import { resolveId } from "@medplum/core";
import { useMedplum } from "@medplum/react-hooks";
import * as Sentry from "@sentry/nextjs";
import { usePathname } from "next/navigation";
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { resolveCanSwitchProfile as resolveCanSwitchProfileAction } from "@/actions/auth/resolve-can-switch-profile";
import useAsyncEffect from "@/lib/hooks/use-async-effect";
import { createLogger } from "@/lib/logger";
import { Member } from "@/models/member/Member";
import { Provider } from "@/models/provider/Provider";

const logger = createLogger("AuthProvider");

//--------------------------------------------------------------------------------------------------
//                                           CONTEXT
//--------------------------------------------------------------------------------------------------
interface AuthContextType {
  provider: Provider | undefined;
  member: Member | undefined;
  isLoading: boolean;
  error: string | null;
  isVibrantAdmin: boolean;
  canSwitchProfile: boolean;
  /** Re-fetch the signed-in patient after profile mutations (e.g. settings). */
  refreshMember: () => Promise<void>;
  /** Re-fetch the signed-in provider after profile mutations (e.g. settings). */
  refreshProvider: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

//--------------------------------------------------------------------------------------------------
//                                      CONTEXT PROVIDER
//--------------------------------------------------------------------------------------------------
/**
 * Provider component that manages authentication state and user profile data.
 *
 * This provider automatically detects whether the current user is a healthcare provider (Practitioner)
 * or a patient (Patient) and fetches the appropriate data model. It includes real-time subscriptions
 * to keep the data synchronized with updates from the server.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  //------------------------------------------------------------------------------------------------
  //                                         DEPENDENCIES
  //------------------------------------------------------------------------------------------------
  const medplum = useMedplum();
  const pathname = usePathname() ?? "";
  /** Public iframe surfaces: no profile-switch UI; ProjectMembership reads often 403 — skip probe */
  const isPublicEmbedRoute = pathname.startsWith("/embed/");
  const profile = medplum.getActiveLogin()?.profile;

  //------------------------------------------------------------------------------------------------
  //                                            STATE
  //------------------------------------------------------------------------------------------------
  const isFetchingRef = useRef(false);
  const [provider, setProvider] = useState<Provider | undefined>();
  const [member, setMember] = useState<Member | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVibrantAdmin, setIsVibrantAdmin] = useState<boolean>(false);
  const [canSwitchProfile, setCanSwitchProfile] = useState<boolean>(false);

  //------------------------------------------------------------------------------------------------
  //                                           FUNCTIONS
  //------------------------------------------------------------------------------------------------
  /**
   * Fetches provider data for the current authenticated practitioner.
   */
  const fetchProvider = useCallback(async (medplum: MedplumClient, providerId: string) => {
    try {
      const provider = await Provider.getById(medplum, providerId);
      setProvider(provider);
      // Compute super-admin status once and cache in context
      try {
        const isAdmin = await provider.isVibrantAdmin(medplum);
        setIsVibrantAdmin(isAdmin);
      } catch {
        setIsVibrantAdmin(false);
      }
    } catch (err) {
      setProvider(undefined);
      setError(err instanceof Error ? err.message : "Failed to fetch provider");
      setIsVibrantAdmin(false);
    }
  }, []);

  /**
   * Fetches member data for the current authenticated patient.
   */
  const fetchMember = useCallback(async (medplum: MedplumClient, memberId: string) => {
    try {
      const member = await Member.getById(medplum, memberId);
      setMember(member);
    } catch (err) {
      setMember(undefined);
      setError(err instanceof Error ? err.message : "Failed to fetch member");
    }
  }, []);

  const refreshMember = useCallback(async () => {
    const profileRef = profile?.reference;
    if (!profileRef?.startsWith("Patient/")) {
      return;
    }
    const memberId = resolveId({ reference: profileRef });
    if (!memberId) {
      return;
    }
    await fetchMember(medplum, memberId);
  }, [profile?.reference, medplum, fetchMember]);

  const refreshProvider = useCallback(async () => {
    const profileRef = profile?.reference;
    if (!profileRef?.startsWith("Practitioner/")) {
      return;
    }
    const providerId = resolveId({ reference: profileRef });
    if (!providerId) {
      return;
    }
    await fetchProvider(medplum, providerId);
  }, [profile?.reference, medplum, fetchProvider]);

  /**
   * Resolves whether the authenticated user can switch profiles.
   *
   * Delegates the `ProjectMembership` lookup to a server action backed by the
   * backend client (ENG-4663) — a patient's own scoped session no longer has
   * `ProjectMembership` access, so this can't be answered from the client.
   */
  const resolveCanSwitchProfile = useCallback(
    async ({ profileReference }: { profileReference?: string }) => {
      if (!profileReference) {
        setCanSwitchProfile(false);
        return;
      }

      try {
        const result = await resolveCanSwitchProfileAction();
        setCanSwitchProfile(result.success ? result.data.canSwitchProfile : false);
      } catch (error) {
        logger.warn("Failed to resolve project memberships for profile switching", {
          profileReference,
          error,
        });
        setCanSwitchProfile(false);
      }
    },
    [],
  );

  //------------------------------------------------------------------------------------------------
  //                                           EFFECTS
  //------------------------------------------------------------------------------------------------

  // Handle profile changes
  useAsyncEffect(async () => {
    // If the user is logged out, reset the auth state.
    if (!profile) {
      setIsLoading(false);
      setError(null);
      setProvider(undefined);
      setMember(undefined);
      setIsVibrantAdmin(false);
      setCanSwitchProfile(false);
      Sentry.setUser(null);
      Sentry.setTag("profileType", undefined);
      Sentry.setTag("providerId", undefined);
      Sentry.setTag("memberId", undefined);
      return;
    }

    // Extract profile information
    const resourceType = profile.reference?.split("/")[0];
    const profileId = resolveId(profile);

    if (!profileId) {
      setError("Failed to resolve profile ID");
      setCanSwitchProfile(false);
      setIsLoading(false);
      return;
    }

    // Attach identity to every subsequent Sentry event for this session — including ones captured
    // via captureConsoleIntegration (e.g. Medplum SDK subscription failures), which otherwise carry
    // no user/practice context and show "Users Impacted: 0" no matter how frequently they fire.
    Sentry.setUser({ id: profileId });
    Sentry.setTag("profileType", resourceType);
    Sentry.setTag("providerId", resourceType === "Practitioner" ? profileId : undefined);
    Sentry.setTag("memberId", resourceType === "Patient" ? profileId : undefined);

    // Check if we're dealing with a different user
    const isProviderProfile = resourceType === "Practitioner";
    const isMemberProfile = resourceType === "Patient";

    // Detect profile changes and reset state accordingly
    if (isProviderProfile && provider?.id !== profileId) {
      // Switching to a different provider or from member to provider
      setMember(undefined);
      setProvider(undefined);
      setIsVibrantAdmin(false);
      setCanSwitchProfile(false);
    } else if (isMemberProfile && member?.id !== profileId) {
      // Switching to a different member or from provider to member
      setProvider(undefined);
      setMember(undefined);
      setIsVibrantAdmin(false);
      setCanSwitchProfile(false);
    }

    // Check if we already have the correct data for the current profile
    const hasCorrectData =
      (isProviderProfile && provider?.id === profileId) ||
      (isMemberProfile && member?.id === profileId);

    if (hasCorrectData) {
      if (!isPublicEmbedRoute) {
        await resolveCanSwitchProfile({ profileReference: profile.reference });
      } else {
        setCanSwitchProfile(false);
      }
      setIsLoading(false);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      if (isProviderProfile) {
        await fetchProvider(medplum, profileId);
      } else if (isMemberProfile) {
        await fetchMember(medplum, profileId);
        setIsVibrantAdmin(false);
      }

      if (!isPublicEmbedRoute) {
        await resolveCanSwitchProfile({ profileReference: profile.reference });
      } else {
        setCanSwitchProfile(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch user data");
      setCanSwitchProfile(false);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [
    profile?.reference,
    medplum,
    provider?.id,
    member?.id,
    fetchProvider,
    fetchMember,
    resolveCanSwitchProfile,
    isPublicEmbedRoute,
  ]);

  // Subscribe to Practitioner updates for providers
  // useSubscription(
  //   profile?.resourceType === "Practitioner" && profile?.id
  //     ? `Practitioner?_id=${profile.id}`
  //     : undefined,
  //   () => profile?.id && fetchProvider(medplum, profile.id),
  // );

  // Subscribe to PractitionerRole updates for providers
  // useSubscription(
  //   profile?.resourceType === "Practitioner" && profile?.id
  //     ? `PractitionerRole?practitioner=Practitioner/${profile.id}`
  //     : undefined,
  //   () => profile?.id && fetchProvider(medplum, profile.id),
  // );

  // Subscribe to Patient updates for members
  // useSubscription(
  //   profile?.resourceType === "Patient" && profile?.id ? `Patient?_id=${profile.id}` : undefined,
  //   () => profile?.id && fetchMember(medplum, profile.id),
  // );

  //------------------------------------------------------------------------------------------------
  //                                        CONTEXT VALUE
  //------------------------------------------------------------------------------------------------
  const contextValue: AuthContextType = {
    provider,
    member,
    isLoading,
    error,
    isVibrantAdmin,
    canSwitchProfile,
    refreshMember,
    refreshProvider,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

//--------------------------------------------------------------------------------------------------
//                                           HOOK
//--------------------------------------------------------------------------------------------------

/**
 * Hook to access the authentication context.
 *
 * @throws Error if used outside of AuthProvider
 * @returns The authentication context containing provider, member, loading state, and error
 *
 * @example
 * ```typescript
 * const { provider, member, isLoading, error } = useAuth();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error}</div>;
 *
 * if (provider) {
 *   return <ProviderDashboard provider={provider} />;
 * }
 *
 * if (member) {
 *   return <MemberDashboard member={member} />;
 * }
 * ```
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

/**
 * Reads auth context when a component can still render without authenticated attribution.
 */
export function useOptionalAuth(): AuthContextType | undefined {
  return useContext(AuthContext);
}
